import { useEffect, useMemo, useRef, useState } from "react";
import { getAthletePositionDrillsAction } from "../../../store/AthleteActions";
import { useDispatch, useSelector } from "react-redux";
import { useAxiosInstance } from "../../../services/Interceptor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faDownload,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons";
import LoadingSpinner from "../../LoadingSpinner";
import CoachingTipsModal from "../../CoachingTipsModal";
import VideoPlayer from "../../VideoPlayer";

const emptyTipsModalState = {
  isOpen: false,
  coachingTip: "",
};

const hasCoachingTip = (coachingTip) => Boolean(coachingTip?.trim());

export default function PositionDrills({ userId }) {
  const dispatch = useDispatch();
  const { positionDrill, loading } = useSelector((state) => state.athletes);
  const apiInstance = useAxiosInstance();

  const [openIndexes, setOpenIndexes] = useState([]);
  const [openDrillIndexes, setOpenDrillIndexes] = useState({});
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [tipsModal, setTipsModal] = useState(emptyTipsModalState);
  const videoRefsMap = useRef({});
  const openIndexesRef = useRef([]);
  const openDrillIndexesRef = useRef({});

  useEffect(() => {
    dispatch(getAthletePositionDrillsAction(apiInstance, userId));
  }, [dispatch, apiInstance, userId]);

  const data = useMemo(() => {
    if (!positionDrill || positionDrill.length === 0) {
      return [];
    }

    const drillMap = new Map();

    positionDrill.forEach((event) => {
      (event.drills || []).forEach((drill) => {
        const position = drill.positionName;
        if (!drillMap.has(position)) {
          drillMap.set(position, new Map());
        }

        const positionDrills = drillMap.get(position);

        if (!positionDrills.has(drill.drillName)) {
          positionDrills.set(drill.drillName, {
            drillName: drill.drillName,
            events: [],
          });
        }

        const repMap = new Map();
        (drill.videos || []).forEach((video) => {
          if (!repMap.has(video.repNumber)) {
            repMap.set(video.repNumber, []);
          }
          repMap.get(video.repNumber).push({
            id: video.id,
            filePath: video.filePath,
            angle: video.angle,
            coachingTip: video.coachingTip ?? drill.coachingTip ?? "",
          });
        });

        positionDrills.get(drill.drillName).events.push({
          name: event.eventName,
          date: event.eventDate,
          reps: Array.from(repMap, ([rep, videos]) => ({ rep, videos })),
        });
      });
    });

    return Array.from(drillMap.entries()).map(([position, drillsMap]) => [
      position,
      Array.from(drillsMap.values()),
    ]);
  }, [positionDrill]);

  useEffect(() => {
    if (data.length === 0) {
      setOpenIndexes([]);
      setOpenDrillIndexes({});
      setTipsModal(emptyTipsModalState);
    }
  }, [data.length]);

  useEffect(() => {
    openIndexesRef.current = openIndexes;
  }, [openIndexes]);

  useEffect(() => {
    openDrillIndexesRef.current = openDrillIndexes;
  }, [openDrillIndexes]);

  const registerVideoRef = (key, element) => {
    if (element) {
      videoRefsMap.current[key] = element;
    } else {
      delete videoRefsMap.current[key];
    }
  };

  const pauseVideosMatching = (prefix) => {
    Object.keys(videoRefsMap.current).forEach((key) => {
      if (key.startsWith(prefix)) {
        videoRefsMap.current[key]?.pause();
      }
    });
  };

  const toggleAccordion = (index) => {
    if (openIndexesRef.current.includes(index)) {
      pauseVideosMatching(`${index}-`);
    }
    setOpenIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  };

  const toggleDrill = (posIndex, drillIndex) => {
    const key = `${posIndex}-${drillIndex}`;
    if (openDrillIndexesRef.current[key]) {
      pauseVideosMatching(`${posIndex}-${drillIndex}-`);
    }
    setOpenDrillIndexes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleDownload = async (filePath, fileName, vidIndex) => {
    try {
      setDownloadingIndex(vidIndex);

      const response = await fetch(filePath);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading file:", error);
    } finally {
      setDownloadingIndex(null);
    }
  };

  const openTipsModal = (coachingTip) => {
    if (!hasCoachingTip(coachingTip)) {
      return;
    }

    setTipsModal({
      isOpen: true,
      coachingTip,
    });
  };

  const closeTipsModal = () => {
    setTipsModal(emptyTipsModalState);
  };

  return (
    <div className="pt-0 px-4 -m-4 mt-0 overflow-y-auto pb-2 xl:pb-0 lg:pb-0">
      {loading ? (
        <LoadingSpinner />
      ) : data.length > 0 ? (
        <div className="mt-3">
          {data.map(([positionName, drills], posIndex) => (
            <div key={positionName} className="mb-2">
              <div className="border rounded shadow-sm space-y-2">
                <button
                  onClick={() => toggleAccordion(posIndex)}
                  className="flex justify-between items-center w-full px-4 py-3 font-semibold bg-gray-100 hover:bg-gray-200 focus:outline-none"
                >
                  {positionName}
                  <FontAwesomeIcon
                    icon={faChevronDown}
                    className={`transition-transform duration-300 ${
                      openIndexes.includes(posIndex) ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>

              {openIndexes.includes(posIndex) && (
                <div>
                  {drills.map((drill, drillIndex) => {
                    const drillKey = `${posIndex}-${drillIndex}`;
                    const isDrillOpen = openDrillIndexes[drillKey];

                    return (
                      <div
                        key={`${positionName}-${drill.drillName}`}
                        className="border shadow-sm mb-6"
                      >
                        <button
                          onClick={() => toggleDrill(posIndex, drillIndex)}
                          className="flex justify-between items-center w-full px-4 py-2 bg-white border-l-4 border-blue-600 hover:bg-gray-100 focus:outline-none"
                        >
                          <h3 className="text-lg font-bold text-blue-800">
                            Drill: {drill.drillName}
                          </h3>
                          <FontAwesomeIcon
                            icon={faChevronDown}
                            className={`transition-transform duration-300 ${
                              isDrillOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {isDrillOpen && (
                          <div className="p-0">
                            {drill.events.map((event, eventIndex) => (
                              <div key={`${event.name}-${event.date}`}>
                                <div className="font-bold bg-gray-200 p-2">
                                  {event.name} - {event.date}
                                </div>
                                {event.reps.map((rep, repIndex) => (
                                  <div key={`${event.name}-${rep.rep}`}>
                                    <div className="bg-gray-100 p-2 font-bold">
                                      Rep {rep.rep}
                                    </div>
                                    <div className="flex flex-wrap space-x-4 mt-2">
                                      {rep.videos.map((vid, vidIndex) => (
                                        <div
                                          key={vid.id ?? vidIndex}
                                          className="p-3"
                                        >
                                          {vid.filePath ? (
                                            <VideoPlayer
                                              src={vid.filePath}
                                              registerVideoRef={(el) =>
                                                registerVideoRef(
                                                  `${posIndex}-${drillIndex}-${eventIndex}-${repIndex}-${vidIndex}`,
                                                  el,
                                                )
                                              }
                                            />
                                          ) : (
                                            <div className="text-gray-500">
                                              No video available
                                            </div>
                                          )}
                                          <div className="mt-2 flex items-center justify-between">
                                            <span className="text-base text-gray-800 truncate max-w-[200px]">
                                              {vid.angle}
                                            </span>
                                            {vid.filePath && (
                                              <button
                                                onClick={() =>
                                                  handleDownload(
                                                    vid.filePath,
                                                    `Drill_${drill.drillName}_Rep${rep.rep}_${vid.angle}.mp4`,
                                                    vidIndex,
                                                  )
                                                }
                                              >
                                                {downloadingIndex ===
                                                vidIndex ? (
                                                  <FontAwesomeIcon
                                                    icon={faSpinner}
                                                    spin
                                                  />
                                                ) : (
                                                  <FontAwesomeIcon
                                                    icon={faDownload}
                                                  />
                                                )}
                                              </button>
                                            )}
                                          </div>
                                          {hasCoachingTip(vid.coachingTip) && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                openTipsModal(vid.coachingTip)
                                              }
                                              className="flex items-center my-3 text-left text-sm font-medium text-gray-600 hover:text-blue-800 hover:underline"
                                            >
                                              <img
                                                className="h-5 pr-2"
                                                alt="View Note"
                                                src="/viewnote.svg"
                                              />{" "}
                                              View Coaching Tips
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="p-3 text-blue-900 bg-blue-100 border border-blue-300">
          There are no Position Drills added.
        </div>
      )}
      <CoachingTipsModal
        isOpen={tipsModal.isOpen}
        title="View Coaching Tips"
        description=""
        coachingTip={tipsModal.coachingTip}
        readOnly
        onClose={closeTipsModal}
      />
    </div>
  );
}
